import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { message } from "antd";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import moment from "moment";
import { getMediaPlanById } from "../../../core/api/mediaplan";
import { getProductsByBrand } from "../../../core/api/products";
import { getPurchaseOrderEvolution } from "../../../core/api/products";
import MailingResponseTable from "./MailingResponseTable";

const ViewMediaPlan = ({ mediaPlanId }) => {
  const [mediaPlan, setMediaPlan] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [brandProducts, setBrandProducts] = useState([]);
  const [purchaseOrderEvolution, setPurchaseOrderEvolution] = useState([]);

  useEffect(() => {
    const fetchPurchaseOrderEvolution = async () => {
      try {
        if (!mediaPlan?.offer_details?.product_variations_detail?.length)
          return;

        const variationIds =
          mediaPlan.offer_details.product_variations_detail.map((v) => v.id);

        const clientId = mediaPlan.offer_details.client.id;
        const response = await getPurchaseOrderEvolution(
          [clientId],
          variationIds
        );
        setPurchaseOrderEvolution(response.data);
      } catch (error) {
        console.error("Error fetching purchase order evolution:", error);
      }
    };

    if (mediaPlan) {
      fetchPurchaseOrderEvolution();
    }
  }, [mediaPlan]);

  console.log("brandProducts", brandProducts);

  const totalFields = [

    "refund_cost",
    "shipping_cost",
    "buyers_amt",
    "cc_buyers_amt",
    "non_cc_buyers_amt",
    "net_amt",
    "units_sold",
    "mail_qty",
    "buyers",
    "cc_buyers",
    "non_cc_buyers",
    "refunds",
    "net",
    "turns",
    "backorders",
    "percent_response_with_bo",
    "misc_fee",
    "cc",
    "non_cc",
    "auto_ships",
    "tot_purch",
    "refunds_amt",
    "mail_processing_cost",
    "call_center_cost",
    "fulfillment_cost",
    "merchant_fee",
    "royalty_cost",
    "net_pl",
    "net_per_order",
    "percent_breakeven",
    "breakeven_orders",
    "rev_per_piece",
    "net_per_piece",
    "avg_order",
    "lists_cost",
    "data_processing_cost",
    "printer_cost",
    "letter_shop_cost",
    "postage_cost",
  ];

  const formatMediaPlanRow = (mediaPlan) => {
    if (!mediaPlan.list_segments || !mediaPlan.list_segments.length) return [];
  
    return mediaPlan.list_segments.map((seg) => ({
      source_code: seg.source_code || null,
      campaign_code: mediaPlan.campaign_code, // This might still come from mediaPlan
      listname: seg.list_details?.name || "-",
      segment: seg.segment_details?.name || "-",
      index: seg.index || null,
      units_sold: seg.units_sold || null,
      mail_qty: seg.mail_qty || null,
      buyers: seg.buyers || null,
      cc_buyers: seg.cc_buyers || null,
      non_cc_buyers: seg.non_cc_buyers || null,
      refunds: seg.refunds || null,
      net: seg.net || null,
      turns: seg.turns || null,
      backorders: seg.backorders || null,
      misc_fee: seg.misc_fee || null,
      call_center_cost: seg.call_center_cost || null,
      fulfillment_cost: seg.fulfillment_cost || null,
      merchant_fee: seg.merchant_fee || null,
      royalty_cost: seg.royalty_cost || null,
      lists_cost: seg.lists_cost || null,
      printer_cost: seg.printer_cost || null,
      postage_cost: seg.postage_cost || null,
      letter_shop_cost: seg.letter_shop_cost || null,
      refund_cost: seg.refund_cost || null,
      shipping_cost: seg.shipping_cost || null,
      buyers_amt: seg.buyers_amt || null,
      cc_buyers_amt: seg.cc_buyers_amt || null,
      non_cc_buyers_amt: seg.non_cc_buyers_amt || null,
      net_amt: seg.net_amt || null,
      data_processing_cost: seg.data_processing_cost || null,
      mail_processing_cost: seg.mail_processing_cost || null,
      product_category: seg.product_category || null, // New field
      performance: seg.performance || null, // New field
      cost: seg.cost || null, // New field
      autoship: seg.autoship || null, // New field
      autoship_amt: seg.autoship_amt || null, // New field
      turns_amt: seg.turns_amt || null, // New field
      backorders_amt: seg.backorders_amt || null, // New field
      product_cost: seg.product_cost || null, // New field
      priority: seg.priority || null,
      test_cont: seg.test_cont || null,
      max_all: seg.max_all || null,
      order_qty: seg.order_qty || null,
      comment: seg.comment || null,
    }));
  };

  const totalsRow = {};

  totalFields.forEach((field) => {
    const total = rowData.reduce((sum, row) => {
      const val = parseFloat(row[field]);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);

    if (field === "percent_response") {
      totalsRow[field] = `${(total / rowData.length).toFixed(2)}%`; //  sum.toFixed(2) if you're summing %
    } else if (field === "percent_response_with_bo") {
      totalsRow[field] = `${(total / rowData.length).toFixed(2)}%`; 
    } 
    else {
      totalsRow[field] = total;
    }
  });

  totalsRow.source_code = "Total";

  useEffect(() => {
    if (mediaPlanId) {
      getMediaPlanById(mediaPlanId)
        .then((res) => {
          const planData = res.data;
          setMediaPlan(planData);
          setRowData(formatMediaPlanRow(planData));
        })
        .catch((error) => {
          console.error(error);
          message.error("Failed to fetch media plan data.");
        });
    }
  }, [mediaPlanId]);

  useEffect(() => {
    const brandId = mediaPlan?.offer_details?.brand;
    if (brandId) {
      getProductsByBrand(brandId)
        .then((res) => setBrandProducts(res.data))
        .catch((err) => {
          console.error("Failed to fetch products by brand", err);
          message.error("Failed to load products for the brand.");
        });
    }
    console.log("mediaPlan", mediaPlan);
  }, [mediaPlan]);

  if (!mediaPlan) return <div className="text-center mt-5">Loading...</div>;

  const mailDate = mediaPlan.date;
  const daysSinceMailDate = mailDate
    ? moment().diff(moment(mailDate), "days")
    : "-";

  const columns = [
    { headerName: "Source Code", field: "source_code" },
    { headerName: "Listname", field: "listname" },
    { headerName: "Segment", field: "segment" },
    { headerName: "Priority", field: "priority" },
    { headerName: "Test/Cont", field: "test_cont" },
    { headerName: "Max/All", field: "max_all" },
    { headerName: "Order Qty", field: "order_qty" },
    { headerName: "Units Sold", field: "units_sold" },
    { headerName: "Qty Mailed", field: "mail_qty" },
    { headerName: "Buyers", field: "buyers" },
    { headerName: "CC Buyers", field: "cc_buyers" },
    { headerName: "Non-CC Buyers", field: "non_cc_buyers" },
    { headerName: "Refunds", field: "refunds" },
    { headerName: "Net", field: "net" },
    { headerName: "Turns", field: "turns" },
    { headerName: "Backorders", field: "backorders" },
    { headerName: "Net Amt", field: "net_amt" },
    { headerName: "Misc Fee", field: "misc_fee" },
    { headerName: "Refunds", field: "refunds_amt" },
    { headerName: "Mailing Cost", field: "mail_processing_cost" },
    { headerName: "Call Center Cost", field: "call_center_cost" },
    { headerName: "Fulfill Cost", field: "fulfillment_cost" },
    { headerName: "Merchant Fee", field: "merchant_fee" },
    { headerName: "Royalties Cost", field: "royalty_cost" },
    { headerName: "List Cost", field: "lists_cost" },
    { headerName: "Data Proc", field: "data_processing_cost" },
    { headerName: "Printer Cost", field: "printer_cost" },
    { headerName: "Letter Shop Cost", field: "letter_shop_cost" },
    { headerName: "Postage Cost", field: "postage_cost" },
  ];

  return (
    <div className="view-media-plan-wrapper p-4">
      <div className="media-plan-details-box shadow-sm p-4 mb-4 bg-white rounded border">
        <div className="row">
          <div className="col-md-3">
            <p>
              <strong>Brand Name:</strong> {mediaPlan?.offer_details.brand_name}
            </p>
          </div>
          <div className="col-md-3">
            <p>
              {" "}
              <strong>Campaign Code: </strong>
              {mediaPlan.campaign_code}
            </p>
          </div>
          <div className="col-md-3">
            <p>
              <strong>Mail Date:</strong>{" "}
              {mailDate ? moment(mailDate).format("MM/DD/YYYY") : "-"}
            </p>
          </div>
          <div className="col-md-3">
            <p>
              <strong>Offer Codification:</strong>{" "}
              {mediaPlan.offer_details?.code_offer || "-"}
            </p>
          </div>
          <div className="col-md-3">
            <p>
              <strong>Product:</strong> {brandProducts[0]?.name || "-"}
            </p>
          </div>
          {/* <div className="col-md-3">
            <p>
              <strong>Days since Mail Date:</strong> {daysSinceMailDate}
            </p>
          </div> */}
          <div className="col-md-3">
            <p>
              <strong>Target Mail Quantity:</strong>{" "}
              {mediaPlan.target_mail_qty || "0"}
            </p>
          </div>
          <div className="col-md-3">
            <p>
              <strong>Actual Mail Quantity:</strong>{" "}
              {mediaPlan.actual_mail_qty || "0"}
            </p>
          </div>
          {/* <div className="col-md-3">
            <p><strong>Rate %:</strong> {mediaPlan.target_rate_of_response || "0"}%</p>
          </div> */}
          <div className="col-md-3">
            <p>
              <strong>% Resp Received:</strong>{" "}
              {mediaPlan.target_rate_of_response || "0"}%
            </p>
          </div>
          <div className="col-md-3">
            <p>
              <strong>Strategy:</strong> {mediaPlan.strategy.strategy || "0"}
            </p>
          </div>
          <div className="col-md-3">
            <p>
              <strong>Status:</strong> {mediaPlan.status.name || "0"}
            </p>
          </div>
          <div className="col-md-3">
            <p>
              <strong>Product Variation:</strong>
              {purchaseOrderEvolution.length > 0
                ? purchaseOrderEvolution
                    .map((item) => `${item.product_variation_code}`)
                    .join(", ")
                : "-"}
            </p>
          </div>
        </div>
      </div>

      <div className="media-plan-aggrid-box mt-4">
        <h5 className="mb-3">Media Plan Metrics</h5>
        <div
          className="ag-theme-quartz"
          style={{ height: "600px", width: "100%", overflowX: "auto" }}>
          <AgGridReact
            columnDefs={columns}
            rowData={rowData}
            pinnedBottomRowData={[totalsRow]}
            defaultColDef={{ flex: 1, minWidth: 140, resizable: true }}
            pagination={false}
            enableCellTextSelection={true}
            ensureDomOrder={true}
          />
        </div>
      </div>
      
      {/* Mailing Response Data Component */}
      <MailingResponseTable mediaPlanId={mediaPlanId} />
    </div>
  );
};

export default ViewMediaPlan;
